import * as Clipboard from 'expo-clipboard';
import { useCallback } from 'react';
import { Linking } from 'react-native';

import { useToaster } from '@/hooks/useToaster';
import { Sentry } from '@/util/sentry';

/**
 * Pulls the bare address out of a `mailto:` link so the fallback can offer
 * something the reader can paste. Falls back to the whole link if it is not
 * shaped like a mailto, which only costs a slightly uglier toast.
 *
 * This runs on the path that recovers from a failed open, so it must not throw:
 * `decodeURIComponent` rejects a lone `%`, and a throw here would escape as the
 * unhandled rejection the hook exists to prevent, leaving no toast at all.
 */
function getEmailAddress(mailtoLink: string) {
  const address = mailtoLink.replace(/^mailto:/i, '').split('?')[0];

  if (!address) {
    return mailtoLink;
  }

  try {
    return decodeURIComponent(address);
  } catch {
    return address;
  }
}

/**
 * Opens a `mailto:` link, with a fallback for devices that cannot handle one.
 *
 * `Linking.openURL` returns a promise that *rejects* when no handler exists —
 * on iOS with no mail account configured, or with the Mail app removed, it
 * rejects with `Unable to open URL: mailto:…`. Call sites used to fire it bare,
 * so the rejection surfaced as an unhandled promise rejection in Sentry and the
 * user got no feedback at all beyond a control that appeared to do nothing.
 *
 * A missing mail handler is a device configuration state rather than an app
 * bug, so it is recorded as a breadcrumb instead of an exception: the recovery
 * is to put the address somewhere the user can paste it and say so.
 */
export function useEmailLink() {
  const toast = useToaster();

  const openEmailLink = useCallback(
    async (mailtoLink: string) => {
      try {
        await Linking.openURL(mailtoLink);

        return true;
      } catch (error) {
        Sentry.addBreadcrumb({
          category: 'email-link',
          level: 'warning',
          message: 'mailto link could not be opened',
          data: { errorMessage: error instanceof Error ? error.message : String(error) },
        });

        const address = getEmailAddress(mailtoLink);

        let copied = false;

        try {
          await Clipboard.setStringAsync(address);

          copied = true;
        } catch (clipboardError) {
          Sentry.captureException(clipboardError, {
            tags: { component: 'useEmailLink', source: 'mailto-fallback' },
            extra: {
              errorMessage:
                clipboardError instanceof Error ? clipboardError.message : String(clipboardError),
            },
          });
        }

        toast.showWarning({
          title: 'No email app available',
          message: copied
            ? `We couldn't open your email app. ${address} is copied to your clipboard.`
            : `We couldn't open your email app. Please email us at ${address}.`,
          duration: 6000,
        });

        return false;
      }
    },
    [toast]
  );

  return { openEmailLink };
}
